import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const LearningPathsSection = ({ selectedRole }) => {
  const [expandedPath, setExpandedPath] = useState(null);

  const learningPaths = [
    {
      id: 'coordinator',
      role: 'coordinator',
      title: 'Carbon Project Coordinator',
      description: 'Master project coordination, stakeholder management, and team leadership',
      duration: '8 weeks',
      modules: 12,
      progress: 65,
      skills: ['Project Management', 'Stakeholder Engagement', 'Team Coordination', 'Risk Assessment'],
      color: 'bg-blue-500'
    },
    {
      id: 'pdd-drafting',
      role: 'specialist',
      title: 'PDD Drafting Specialist',
      description: 'Learn to create comprehensive Project Design Documents for carbon projects',
      duration: '6 weeks',
      modules: 10,
      progress: 45,
      skills: ['Technical Writing', 'Methodology Application', 'Baseline Analysis', 'Additionality Assessment'],
      color: 'bg-green-500'
    },
    {
      id: 'mrv',
      role: 'validator',
      title: 'MRV Specialist',
      description: 'Monitoring, Reporting, and Verification expertise for carbon projects',
      duration: '10 weeks',
      modules: 15,
      progress: 30,
      skills: ['Data Collection', 'Quality Assurance', 'Verification Protocols', 'Reporting Standards'],
      color: 'bg-purple-500'
    },
    {
      id: 'geospatial',
      role: 'analyst',
      title: 'Geospatial Analysis',
      description: 'Remote sensing and GIS analysis for carbon project assessment',
      duration: '12 weeks',
      modules: 18,
      progress: 55,
      skills: ['GIS Software', 'Remote Sensing', 'Spatial Analysis', 'Data Visualization'],
      color: 'bg-orange-500'
    },
    {
      id: 'regulatory',
      role: 'auditor',
      title: 'Regulatory & Art. 6 Compliance',
      description: 'Navigate international carbon market regulations and Article 6 mechanisms',
      duration: '7 weeks',
      modules: 11,
      progress: 20,
      skills: ['Policy Analysis', 'Compliance Framework', 'International Standards', 'Legal Requirements'],
      color: 'bg-red-500'
    }
  ];

  const filteredPaths = selectedRole === 'all' 
    ? learningPaths 
    : learningPaths?.filter(path => path?.role === selectedRole);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-semibold text-foreground">Role-Specific Learning Paths</h2>
        <span className="text-sm text-muted-foreground">
          {filteredPaths?.length} path{filteredPaths?.length !== 1 ? 's' : ''} available
        </span>
      </div>

      <div className="grid gap-4">
        {filteredPaths?.map(path => (
          <div
            key={path?.id}
            className="bg-card border border-border rounded-lg p-6 hover:shadow-md transition-shadow"
          >
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <div className={`w-3 h-3 rounded-full ${path?.color}`} />
                  <h3 className="text-lg font-semibold text-foreground">{path?.title}</h3>
                  <span className="px-2 py-1 bg-muted rounded text-xs font-medium text-muted-foreground">
                    {path?.role?.charAt(0)?.toUpperCase() + path?.role?.slice(1)}
                  </span>
                </div>
                <p className="text-muted-foreground text-sm mb-3">{path?.description}</p>
                <div className="flex items-center gap-4 text-sm text-muted-foreground">
                  <div className="flex items-center gap-1">
                    <Icon name="Clock" size={16} />
                    <span>{path?.duration}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Icon name="BookOpen" size={16} />
                    <span>{path?.modules} modules</span>
                  </div>
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                iconName={expandedPath === path?.id ? 'ChevronUp' : 'ChevronDown'}
                onClick={() => setExpandedPath(expandedPath === path?.id ? null : path?.id)}
              >
                {expandedPath === path?.id ? 'Hide' : 'Details'}
              </Button>
            </div>

            {/* Progress Bar */}
            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-foreground">Progress</span>
                <span className="text-sm font-medium text-primary">{path?.progress}%</span>
              </div>
              <div className="w-full bg-muted rounded-full h-2">
                <div
                  className={`h-2 rounded-full ${path?.color}`}
                  style={{ width: `${path?.progress}%` }}
                />
              </div>
            </div>

            {/* Expanded Details */}
            {expandedPath === path?.id && (
              <div className="pt-4 border-t border-border space-y-4">
                <div>
                  <h4 className="text-sm font-semibold text-foreground mb-2">Key Skills</h4>
                  <div className="flex flex-wrap gap-2">
                    {path?.skills?.map((skill, idx) => (
                      <span
                        key={idx}
                        className="px-3 py-1 bg-primary/10 text-primary rounded-full text-xs font-medium"
                      >
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button variant="default" size="sm" iconName="Play">
                    {path?.progress > 0 ? 'Continue Learning' : 'Start Path'}
                  </Button>
                  <Button variant="outline" size="sm" iconName="Bookmark">
                    Bookmark
                  </Button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {filteredPaths?.length === 0 && (
        <div className="text-center py-12 bg-muted/30 rounded-lg">
          <Icon name="BookOpen" size={48} className="mx-auto mb-4 text-muted-foreground" />
          <p className="text-muted-foreground">No learning paths available for this role</p>
        </div>
      )}
    </div>
  );
};

export default LearningPathsSection;